import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Checkbox,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { documentAction } from 'actions';
import AmatriumSkeleton from 'components/AmatriumSkeleton';
import Empty from 'components/Empty';
import { DEFAULT_BEGIN_PAGE, DEFAULT_SIZE_PAGE } from 'helpers/constant';
import { dateFormat } from 'helpers/date';
import { ROLES } from 'helpers/enum';
import { encodeURIFileName } from 'helpers/file';
import { getUserRolesStorage } from 'helpers/lib';
import { getStorage, setStorage, STORAGE_KEY } from 'helpers/storage';
import { useDispatch } from 'react-redux';
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

const AmatriumTableDocument = (props) => {
  const { columns, data, documentName, rowKey, onRowSelect, isLoading } = props;
  const dispatch = useDispatch();
  const {
    getTableBodyProps, // table body props from react-table
    headerGroups, // headerGroups, if your table has groupings
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // DS surface tokens so dark mode matches the page (no washed-out gray).
  // Mockup: transparent header (no solid gray fill band), gray uppercase labels.
  const theadBackgroundColor = 'transparent';
  const tableBackgroundColor = 'bg.surface';
  const trBackgroundColor = 'bg.canvas';

  const userRoles = getUserRolesStorage();
  const isAdmin = userRoles?.includes(ROLES.ADMIN) || userRoles?.includes(ROLES.SUPER_ADMIN);

  if (data?.length && data.length > 0 && data.length < DEFAULT_SIZE_PAGE) {
    const arrKeys = Object.keys(data[0]);
    const arrNotHaveData = [];
    for (let i = 0; i < DEFAULT_SIZE_PAGE - data.length; i++) {
      const objNotHaveData = {};
      arrKeys.forEach((item) => {
        objNotHaveData[item] = '';
      });
      arrNotHaveData.push(objNotHaveData);
    }
    data.push(...arrNotHaveData);
  }

  const showText = (col, row) => {
    return col.render ? (
      col.render(row)
    ) : row[col.key] && col.key === 'created_date' ? (
      dateFormat(row[col.key])
    ) : row[col.key] !== '' ? (
      row[col.key]
    ) : (
      <Box py={{ base: '0.55rem' }} fontSize={14} />
    );
  };

  const getNewData = (currentIndex) => {
    if (currentIndex > 0 && headerGroups?.length > 0 && headerGroups[0].headers.length > 0) {
      const tempSortField =
        headerGroups[0].headers[currentIndex]?.isSortedDesc === undefined ||
        headerGroups[0].headers[currentIndex]?.isSortedDesc === false
          ? headerGroups[0].headers[currentIndex].key === 'created_date'
            ? 'created_at'
            : headerGroups[0].headers[currentIndex].key
          : '';

      const tempIsAscending =
        getStorage(STORAGE_KEY.SORT_FIELD_INDEX) !== currentIndex.toString()
          ? 'true'
          : getStorage(STORAGE_KEY.IS_ASCENDING) === 'false'
            ? ''
            : getStorage(STORAGE_KEY.IS_ASCENDING) === 'true'
              ? false
              : getStorage(STORAGE_KEY.IS_ASCENDING) === '';
      setStorage(STORAGE_KEY.SORT_FIELD_INDEX, currentIndex);
      setStorage(STORAGE_KEY.SORT_FIELD, tempSortField);
      setStorage(STORAGE_KEY.IS_ASCENDING, tempIsAscending);
      dispatch(
        documentAction.fetchFiles({
          page: DEFAULT_BEGIN_PAGE,
          fileName: encodeURIFileName(documentName),
          sortField: tempSortField,
          isAscending: tempIsAscending,
        })
      );
    }
  };

  return (
    // overflowX:auto contains any data-driven width overflow (long filenames/emails) inside the
    // table area instead of letting it bleed past the viewport right edge (UI-003).
    <Box overflowX='auto'>
      {isLoading ? (
        <AmatriumSkeleton options={null} />
      ) : data?.length ? (
        <Table
          display='table'
          w='100%'
          minW={{ base: '720px', lg: '0' }}
          // Fit naturally on desktop (no forced horizontal scroll). Only enforce a
          // minimum width below `lg` so the wrapper's overflowX kicks in as a
          // tablet/mobile fallback — never on desktop.
          bg={tableBackgroundColor}
          borderWidth={props.borderWidth ?? '0'}
          style={{ tableLayout: 'auto', width: '100%' }}
        >
          <Thead bg={theadBackgroundColor}>
            {headerGroups.map((headerGroup, index) => (
              <Tr key={index} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <Th
                    key={column.key}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    {...column.getHeaderProps([{ className: column.className }])}
                    w={column.width}
                    minW={column.minWidth}
                    maxW={column.maxWidth}
                    color='text.subtle'
                    fontSize='xs'
                    fontWeight={600}
                    letterSpacing='0.04em'
                    textTransform='uppercase'
                    borderColor='border.default'
                    whiteSpace='nowrap'
                  >
                    <Flex
                      align={'center'}
                      gap={'10px'}
                      onClick={() => column.canSort && getNewData(index)}
                    >
                      <Box as='span'>
                        {column?.type === 'checkbox' ? (
                          <Checkbox borderColor='gray.200' onChange={column?.titleClickHandler} />
                        ) : (
                          column.render('Header')
                        )}
                      </Box>
                      <Box as='span'>
                        {column.isSorted === true
                          ? column?.isSortedDesc === true && <TriangleDownIcon boxSize={3} ml={2} />
                          : column?.isSortedDesc === false && <TriangleUpIcon boxSize={3} ml={2} />}
                        {!column.isSorted &&
                          index.toString() === getStorage(STORAGE_KEY.SORT_FIELD_INDEX) &&
                          getStorage(STORAGE_KEY.IS_ASCENDING) === 'true' && (
                            <TriangleUpIcon boxSize={3} ml={2} />
                          )}
                        {!column.isSorted &&
                          index.toString() === getStorage(STORAGE_KEY.SORT_FIELD_INDEX) &&
                          getStorage(STORAGE_KEY.IS_ASCENDING) === 'false' && (
                            <TriangleDownIcon boxSize={3} ml={2} />
                          )}
                      </Box>
                      <Box as='span' ml={2}>
                        {column?.canFilter ? column.render('Filter') : null}
                      </Box>
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {data?.map((row, idRow) => {
              return (
                <Tr
                  key={rowKey ? row[rowKey] : row?.key ? row.key : idRow}
                  bg={row?.active ? trBackgroundColor : 'transparent'}
                  _hover={
                    onRowSelect
                      ? {
                          bg: trBackgroundColor,
                          cursor: 'pointer',
                        }
                      : {}
                  }
                  onClick={() => {
                    if (onRowSelect) {
                      onRowSelect(row, idRow);
                    }
                  }}
                >
                  {columns?.map((col) => {
                    if (col.key === 'action') {
                      return (
                        <Td key={col.key} ps={3}>
                          <Flex align='center' justify='flex-start' ml={!isAdmin && 6}>
                            {row?.id &&
                              col?.actionItems?.map((item) => (
                                <div key={item.key}>
                                  <Tooltip label={item.label}>
                                    <IconButton
                                      minW='28px'
                                      h='28px'
                                      color={item.color}
                                      fontSize={18}
                                      bg='none'
                                      _hover={{ bg: 'none', opacity: '0.6' }}
                                      aria-label={item['aria-label']}
                                      icon={item.icon}
                                      onClick={(e) => item.onClick(e, row)}
                                    />
                                  </Tooltip>
                                </div>
                              ))}
                          </Flex>
                        </Td>
                      );
                    }
                    // Columns flagged `truncate` (name / uploadedBy) get a max
                    // width + ellipsis so long values (e.g. full emails) never
                    // force the table past the page width on desktop.
                    return col?.key ? (
                      <Td
                        key={col?.key}
                        overflow={col.truncate ? 'hidden' : undefined}
                        w={col.width}
                        maxW={col.maxWidth ?? (col.truncate ? '1px' : undefined)}
                        py={{
                          base: '0.75rem',
                          lg: '1rem',
                        }}
                        color='text.default'
                        fontSize={14}
                        borderColor='border.default'
                        whiteSpace={col.truncate ? 'normal' : 'nowrap'}
                      >
                        {col?.type === 'checkbox' ? (
                          <>
                            {row?.id && (
                              <Checkbox
                                borderColor='gray.200'
                                pointerEvents='none'
                                isChecked={row[col.key]}
                              />
                            )}
                          </>
                        ) : col?.className && row[col.key] !== '' ? (
                          <Tooltip label={showText(col, row)} placement='bottom-start'>
                            <Text isTruncated>
                              {col.render ? (
                                col.render(row)
                              ) : row[col.key] && col.key === 'created_date' ? (
                                dateFormat(row[col.key])
                              ) : row[col.key] !== '' ? (
                                row[col.key]
                              ) : (
                                <Box py={{ base: '0.55rem' }} fontSize={14} />
                              )}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Text>
                            {col.render ? (
                              col.render(row)
                            ) : row[col.key] && col.key === 'created_date' ? (
                              dateFormat(row[col.key])
                            ) : row[col.key] !== '' ? (
                              row[col.key]
                            ) : (
                              <Box py={{ base: '0.55rem' }} fontSize={14} />
                            )}
                          </Text>
                        )}
                      </Td>
                    ) : null;
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      ) : (
        <Empty />
      )}
    </Box>
  );
};

export default AmatriumTableDocument;
